import { MP_COLORS, MP_TEXT_COLORS } from '../../utils/productImage';
import { MP_LABEL } from './msAssortmentApi.mjs';

export { MP_LABEL };
export const MP_SHORT = { wb: 'WB', ozon: 'OZ', yandex: 'YA' };
export const MP_ORDER = ['wb', 'ozon', 'yandex'];
const MP_CODE = { wb: 1, ozon: 2, yandex: 3 };

export const SEVERITY_ORDER = { error: 0, warning: 1, info: 2 };
export const ARCHIVE_CODES = ['MS_ARCHIVED', 'MS_MISSING'];

export const SEGMENTS = [
  { value: 'all', label: 'Все' },
  { value: 'mapped', label: 'Со связями' },
  { value: 'unmapped', label: 'Без связей' },
  { value: 'problems', label: 'С проблемами' },
];

export const FILTER_DEFAULTS = Object.freeze({
  marketplace: '',
  accountId: '',
  problemCodes: [],
  broadcast: '',
  withForced: false,
  withoutPlan: false,
  withMinMax: false,
  includeArchived: false,
});

export const BROADCAST_LABEL = { on: 'Трансляция включена', off: 'Есть выключенные связи' };

const TYPE_LABEL = { product: 'Товар', variant: 'Модификация', bundle: 'Комплект', service: 'Услуга' };

export function mpChipSx(marketplace) {
  const code = MP_CODE[marketplace];
  return {
    bgcolor: MP_COLORS[code] || 'grey.400',
    color: MP_TEXT_COLORS[code] || '#fff',
    fontWeight: 600,
  };
}

export function typeLabel(type) {
  return TYPE_LABEL[String(type || '').toLowerCase()] || type || 'Товар';
}

export function severityColor(severity) {
  return ['error', 'warning', 'info'].includes(severity) ? severity : 'default';
}

export function issueCounts(issues) {
  const out = { error: 0, warning: 0, info: 0 };
  for (const issue of issues || []) {
    if (out[issue.severity] != null) out[issue.severity] += Number(issue.count) || 1;
  }
  return out;
}

export function worstSeverity(issues) {
  let worst = null;
  for (const issue of issues || []) {
    if (worst == null || (SEVERITY_ORDER[issue.severity] ?? 9) < (SEVERITY_ORDER[worst] ?? 9)) worst = issue.severity;
  }
  return worst;
}

export function sortIssues(issues) {
  return [...(issues || [])].sort((a, b) => (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9));
}

/** Проблемы, которые стоит показывать: у архивного товара его собственный «архив» не дублируем, если связь выключена. */
export function visibleIssues(row) {
  const archived = !!row?.ms_archived;
  return (row?.issues || []).filter((i) => !(archived && i.code === 'MS_ARCHIVED' && i.severity !== 'error'));
}

export function linkKey(link) {
  return `${link.marketplace}:${link.account_id}:${link.product_key}`;
}

export function sameLink(a, b) {
  return a.marketplace === b.marketplace
    && Number(a.account_id) === Number(b.account_id)
    && String(a.product_key) === String(b.product_key);
}

export function linkSkuLabel(link) {
  if (link?.sku != null && String(link.sku).trim()) return String(link.sku).trim();
  if (String(link?.marketplace || '').toLowerCase() === 'yandex') return '';
  return link?.product_key ? String(link.product_key) : '';
}

export function linkMatchesQuery(link, query) {
  const needle = String(query || '').trim().toLowerCase();
  if (!needle) return false;
  return [link?.article, link?.sku, link?.product_key, link?.offer_id]
    .some((value) => String(value || '').toLowerCase().includes(needle));
}

export function linkBarcodes(link) {
  if (Array.isArray(link?.barcodes) && link.barcodes.length) return link.barcodes;
  return link?.barcode ? [link.barcode] : [];
}

export function barcodeMatches(msBarcodes, cardBarcodes) {
  const ms = new Set((msBarcodes || []).map((b) => String(b).trim().toLowerCase()));
  if (!ms.size || !(cardBarcodes || []).length) return null;
  return cardBarcodes.some((b) => ms.has(String(b).trim().toLowerCase()));
}

/** Статистика связей по МП для строки списка: [{marketplace, total, enabled}]. */
export function linkStatsByMp(links) {
  const map = new Map();
  for (const link of links || []) {
    const entry = map.get(link.marketplace) || { marketplace: link.marketplace, total: 0, enabled: 0 };
    entry.total += 1;
    if (link.enabled) entry.enabled += 1;
    map.set(link.marketplace, entry);
  }
  return MP_ORDER.filter((mp) => map.has(mp)).map((mp) => map.get(mp));
}

/** Связи по МП → кабинет. */
export function groupLinks(links) {
  const byMp = new Map();
  for (const link of links || []) {
    if (!byMp.has(link.marketplace)) byMp.set(link.marketplace, new Map());
    const byAcc = byMp.get(link.marketplace);
    const acc = String(link.account_id);
    if (!byAcc.has(acc)) {
      byAcc.set(acc, {
        account_id: link.account_id,
        account_name: link.account_name || `Кабинет ${link.account_id}`,
        account_active: link.account_active !== false,
        links: [],
      });
    }
    byAcc.get(acc).links.push(link);
  }
  const order = [...MP_ORDER, ...[...byMp.keys()].filter((mp) => !MP_ORDER.includes(mp))];
  return order
    .filter((mp) => byMp.has(mp))
    .map((mp) => ({ marketplace: mp, cabinets: [...byMp.get(mp).values()] }));
}

export function forcedForAccount(forced, accountId) {
  return (forced || []).filter((f) => Number(f.account_id) === Number(accountId));
}

export function hasForcedValue(item) {
  return item != null && item.forced_stock != null && String(item.forced_stock) !== '';
}

/** 0 — принудительный ноль, null/'' — принуд не задан (уходит живой остаток). */
export function forcedValueLabel(value, emptyLabel = 'живой') {
  if (value == null || value === '') return emptyLabel;
  return Number(value) === 0 ? '0 (принудительно)' : String(value);
}

/** Режимы трансляции остатка по паре товар×складская связка. */
export const STOCK_MODE_LABEL = {
  live: 'Живой остаток',
  forced: 'Принуд',
  none: 'Не транслировать (0)',
};
export const STOCK_MODE_OPTIONS = [
  { value: 'default', label: 'Как у связки' },
  { value: 'live', label: 'Живой остаток' },
  { value: 'forced', label: 'Принуд N' },
  { value: 'none', label: 'Не транслировать (0)' },
];
export const BINDING_DEFAULT_MODE_OPTIONS = [
  { value: 'live', label: 'Живой остаток' },
  { value: 'none', label: 'Не транслировать (0)' },
];
export const MODE_SOURCE_LABEL = { own: 'своя настройка', binding: 'режим связки по умолчанию' };

/** Эффективный режим элемента forced[] (с сервера) или вычисленный по полям. */
export function effectiveMode(item) {
  if (!item) return { mode: 'live', forced_stock: null, source: 'binding' };
  if (item.effective?.mode) return item.effective;
  if (item.mode === 'forced' || (item.mode == null && hasForcedValue(item))) {
    return { mode: 'forced', forced_stock: Number(item.forced_stock), source: 'own' };
  }
  if (item.mode === 'none') return { mode: 'none', forced_stock: 0, source: 'own' };
  if (item.mode === 'live') return { mode: 'live', forced_stock: null, source: 'own' };
  const def = item.binding_default_mode === 'none' ? 'none' : 'live';
  return { mode: def, forced_stock: def === 'none' ? 0 : null, source: 'binding' };
}

/** «живой», «принуд 30», «не транслируется». */
export function stockModeLabel(eff) {
  if (!eff) return '';
  if (eff.mode === 'forced') return `принуд ${eff.forced_stock ?? 0}`;
  if (eff.mode === 'none') return 'не транслируется';
  return 'живой';
}

/** Цвет/вариант чипа режима: принуд — filled primary, none — filled default (серый), живой — outlined. */
export function stockModeChipProps(eff) {
  if (eff?.mode === 'forced') return { color: 'primary', variant: 'filled' };
  if (eff?.mode === 'none') return { color: 'default', variant: 'filled' };
  return { color: eff?.source === 'own' ? 'success' : 'default', variant: 'outlined' };
}

export function countActiveFilters(filters) {
  let n = 0;
  if (filters.marketplace) n += 1;
  if (filters.accountId) n += 1;
  n += filters.problemCodes.length;
  if (filters.broadcast) n += 1;
  if (filters.withForced) n += 1;
  if (filters.withoutPlan) n += 1;
  if (filters.withMinMax) n += 1;
  if (filters.includeArchived) n += 1;
  return n;
}

export function listParams({ q, segment, filters, offset, limit }) {
  const problems = filters.problemCodes;
  return {
    q: q || undefined,
    limit,
    offset,
    mapped_only: segment === 'mapped' ? 1 : undefined,
    unmapped_only: segment === 'unmapped' ? 1 : undefined,
    problems_any: segment === 'problems' && !problems.length ? 1 : undefined,
    problems: problems.length ? problems.join(',') : undefined,
    problems_account_id: (problems.length || segment === 'problems') && filters.accountId ? filters.accountId : undefined,
    marketplace: filters.marketplace || undefined,
    account_id: filters.accountId || undefined,
    broadcast: filters.broadcast || undefined,
    with_forced: filters.withForced ? 1 : undefined,
    without_plan: filters.withoutPlan ? 1 : undefined,
    with_minmax: filters.withMinMax ? 1 : undefined,
    include_archived_ms: filters.includeArchived ? 1 : undefined,
  };
}

export function formatSyncTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function ruPlural(n, one, few, many) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} ${one}`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} ${few}`;
  return `${n} ${many}`;
}

export function broadcastResultText(enabled, updated, skipped) {
  if (!enabled) return `Трансляция выключена (${updated} связей)`;
  return skipped ? `Включено ${updated}, пропущено ${skipped} (проблемные связи)` : `Трансляция включена (${updated} связей)`;
}

export function shopLabel(shop) {
  return shop === 'sewing' ? 'Швейка' : 'Производство';
}

export function errorText(error, fallback) {
  return error?.response?.data?.message || error?.message || fallback;
}
